import React, { useState, useEffect } from 'react';
import { PageHeader } from './ui/PageHeader';
import apiService from '../services/ApiService';
import { useToast } from '../contexts/ToastContext';

interface ActivityLogEntry {
  id: number;
  admin_user_id: number;
  admin_username: string;
  action: string;
  target_type: string;
  target_id: number;
  details: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

export const ActivityLog: React.FC = () => {
  const { showToast } = useToast();
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [targetTypeFilter, setTargetTypeFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all'); // all, today, week, month
  
  const limit = 20;

  const loadActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Snapshot from analytics — not a full paginated activity API yet.
      const analytics = await apiService.getAdminAnalytics();
      let filteredActivities = Array.isArray(analytics.recent_activity)
        ? analytics.recent_activity
        : [];

      if (search) {
        filteredActivities = filteredActivities.filter(
          (activity: ActivityLogEntry) =>
            activity.admin_username?.toLowerCase().includes(search.toLowerCase()) ||
            activity.action?.toLowerCase().includes(search.toLowerCase()) ||
            activity.target_type?.toLowerCase().includes(search.toLowerCase())
        );
      }

      if (actionFilter !== 'all') {
        filteredActivities = filteredActivities.filter((activity: ActivityLogEntry) =>
          activity.action?.toLowerCase().includes(actionFilter.toLowerCase())
        );
      }

      if (targetTypeFilter !== 'all') {
        filteredActivities = filteredActivities.filter(
          (activity: ActivityLogEntry) =>
            activity.target_type?.toLowerCase() === targetTypeFilter.toLowerCase()
        );
      }

      if (dateFilter !== 'all') {
        const now = new Date();
        const filterDate = new Date();
        switch (dateFilter) {
          case 'today':
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            filterDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            filterDate.setMonth(now.getMonth() - 1);
            break;
        }
        filteredActivities = filteredActivities.filter(
          (activity: ActivityLogEntry) => new Date(activity.created_at) >= filterDate
        );
      }

      setActivities(filteredActivities);
      setTotalPages(Math.max(1, Math.ceil(filteredActivities.length / limit)));
    } catch (err: any) {
      console.error('Failed to load activity log:', err);
      setError(err.message || 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when filters change
  }, [search, actionFilter, targetTypeFilter, dateFilter]);

  const handleExportActivities = () => {
    try {
      const exportData = activities.map(activity => ({
        timestamp: activity.created_at,
        admin: activity.admin_username,
        action: activity.action,
        target_type: activity.target_type,
        target_id: activity.target_id,
        details: activity.details,
        ip_address: activity.ip_address
      }));

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `built-activity-log-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to export activities:', err);
      showToast(err.message || 'Unable to export activities.', 'error');
    }
  };

  const getActionIcon = (action: string) => {
    if (action.includes('create')) return '➕';
    if (action.includes('update') || action.includes('edit')) return '✏️';
    if (action.includes('delete')) return '🗑️';
    if (action.includes('login')) return '🔑';
    if (action.includes('promote') || action.includes('role')) return '👑';
    return '📝';
  };

  const getActionColor = (action: string) => {
    if (action.includes('create')) return 'text-green-400';
    if (action.includes('update') || action.includes('edit')) return 'text-blue-400';
    if (action.includes('delete')) return 'text-red-400';
    if (action.includes('login')) return 'text-orange-400';
    if (action.includes('promote') || action.includes('role')) return 'text-yellow-400';
    return 'text-gray-400';
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const paginatedActivities = activities.slice(
    (currentPage - 1) * limit,
    currentPage * limit
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading activity log...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-red-400 font-medium">Failed to load activity log</h3>
            <p className="text-gray-300 text-sm mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={loadActivities}
          className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div
        role="status"
        className="rounded-xl border border-amber-500/25 bg-amber-500/[0.07] px-4 py-3 text-sm text-amber-100/90"
      >
        Showing a recent activity snapshot from analytics (limited window). This is not a full
        searchable audit archive yet. Export reflects the filtered snapshot only.
      </div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <PageHeader
            eyebrow="Admin"
            title="Activity log"
            description="Recent platform activity snapshot."
            backLink={{ to: '/admin', label: 'Overview' }}
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadActivities}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <button
            onClick={handleExportActivities}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#141414] rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search activities..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Action</label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="input"
            >
              <option value="all">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="login">Login</option>
              <option value="role">Role Changes</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Target Type</label>
            <select
              value={targetTypeFilter}
              onChange={(e) => setTargetTypeFilter(e.target.value)}
              className="input"
            >
              <option value="all">All Types</option>
              <option value="user">Users</option>
              <option value="registration_code">Registration Codes</option>
              <option value="vehicle">Vehicles</option>
              <option value="modification">Modifications</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Time Period</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="input"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
            </select>
          </div>
        </div>
      </div>

      {/* Activity Feed */}
      <div className="bg-[#141414] rounded-lg">
        {paginatedActivities.length === 0 ? (
          <div className="p-8 text-center">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-400 mb-2">No activities in this snapshot</h3>
            <p className="text-gray-500">
              Nothing matched your filters in the recent analytics activity window. Clear filters or
              check again after admin actions are logged.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.08]">
            {paginatedActivities.map((activity) => (
              <div key={activity.id} className="p-6 hover:bg-[#161616]/50 transition-colors">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-[#161616] rounded-full flex items-center justify-center">
                      <span className="text-lg">{getActionIcon(activity.action)}</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-white">{activity.admin_username}</span>
                        <span className={`font-medium ${getActionColor(activity.action)}`}>
                          {activity.action}
                        </span>
                        <span className="text-gray-400">{activity.target_type}</span>
                        {activity.target_id && (
                          <span className="text-gray-500">#{activity.target_id}</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-3 text-sm text-gray-500">
                        <span>{formatTimeAgo(activity.created_at)}</span>
                        <span>{new Date(activity.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    {activity.details && (
                      <p className="text-gray-400 text-sm mt-1">{activity.details}</p>
                    )}
                    <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                      <span>IP: {activity.ip_address}</span>
                      <span className="truncate max-w-xs">
                        User Agent: {activity.user_agent}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, activities.length)} of {activities.length} activities
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="btn-secondary btn-sm"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 rounded-lg transition-colors ${
                  currentPage === page
                    ? 'bg-orange-600 text-white'
                    : 'bg-[#161616] text-white hover:bg-white/[0.06]'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 